import toast from 'react-hot-toast'
import {
  type TrainingMatrixViewDto,
  useCreateTrainingMatrixViewMutation,
  useDeleteTrainingMatrixMutation,
  useUpdateTrainingMatrixMutation
} from '../../../../API/rtk-query-api/store/tmsApi'
import useFormattedFilters from '../../../../mixins/useTMFilters'
import { useTMViewsManagement } from '../../../../mixins/useTMViewsManagement'
import { useTrainingMatrixViews } from '../../../../mixins/useTrainingMatrixViews'
import { trainingMatrixActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import SaveViewModal, { saveViewToasterId } from '../../../UI/view-management/SaveViewModal'
import ViewManagementModal, { manageViewsToasterId } from '../../../UI/view-management/view-management-modal'

const TrainingMatrixViewManagementModal = () => {
  const filters = useFormattedFilters()
  const viewManagement = useTMViewsManagement()

  const dispatch = useAppDispatch()
  const trainingMatrixState = useAppSelector(state => state.trainingMatrix)
  const trainingMatrixFiltersState = useAppSelector(state => state.trainingMatrixFilters)

  const { views } = useTrainingMatrixViews()

  const [saveView, { isLoading: isSavingView }] = useCreateTrainingMatrixViewMutation()
  const [updateView] = useUpdateTrainingMatrixMutation()
  const [deleteView] = useDeleteTrainingMatrixMutation()

  const isDuplicatedViewName = (title: string | undefined, id?: string | undefined) => {
    return views.filter(view => view.id !== id).some(view => view.title === title)
  }

  const onSaveView = (name: string, isGlobal: boolean) => {
    if (name === '') {
      toast.error('Name is required', { toasterId: saveViewToasterId })
    } else if (isDuplicatedViewName(name)) {
      toast.error('A view with that name already exists', { toasterId: saveViewToasterId })
    } else {
      saveView({
        trainingMatrixViewDto: {
          title: name,
          global: isGlobal,
          settings: {
            angleCompetencyHeaders: trainingMatrixFiltersState.slantHeaders,
            expirationDateLimit: trainingMatrixFiltersState.expirationDateLimit,
            gridlineFrequency: trainingMatrixFiltersState.gridlinesFrequency,
            showCompetencyGroups: trainingMatrixFiltersState.showGroups,
            showDates: trainingMatrixFiltersState.showDates,
            exportFormat: trainingMatrixFiltersState.exportFormat,
            showGridlines: trainingMatrixFiltersState.showGridSeparator,
            showDesiredCompetencies: trainingMatrixFiltersState.showDesiredCompetencies,
            // Compliance state:
            showOverallCompliancePercentage: trainingMatrixFiltersState.showOverallCompliancePercentage,
            showPercentageOfComplianceForIndividuals:
              trainingMatrixFiltersState.showPercentageOfComplianceForIndividuals,
            showPercentageOfComplianceForCompetencies:
              trainingMatrixFiltersState.showPercentageOfComplianceForCompetencies,
            showPercentageOfComplianceForCompetencyGroups:
              trainingMatrixFiltersState.showPercentageOfComplianceForGroups,
            isManagerColumnSelected: trainingMatrixFiltersState.showManagersColumn,
            selectedColumns: trainingMatrixFiltersState.columnsToShow.map(column => column.value),
            useColorBlindPalette: trainingMatrixFiltersState.isColourBlindThemeEnabled
          },
          filters: { ...filters }
        }
      })
        .unwrap()
        .then(
          response => {
            dispatch(
              trainingMatrixActions.setSelectedView({
                value: response.id ?? '',
                text: response.title ?? ''
              })
            )
            dispatch(trainingMatrixActions.setShowSaveViewModal(false))
            toast.success('View has been saved')
          },
          response => {
            const errors = response?.data?.validationStatus?.errors
            if (errors && Array.isArray(errors)) {
              if (errors[0] === 'A view with this name already exists in this scope!') {
                toast.error('A view with that name already exists', { toasterId: saveViewToasterId })
              }
            } else {
              toast.error('Save failed', { toasterId: saveViewToasterId })
            }
          }
        )
    }
  }

  const onUpdateView = (updatedView: TrainingMatrixViewDto) => {
    if (updatedView.id) {
      updateView({
        id: updatedView.id,
        trainingMatrixViewDto: {
          title: updatedView.title,
          global: updatedView.global,
          settings: {
            angleCompetencyHeaders: trainingMatrixFiltersState.slantHeaders,
            gridlineFrequency: trainingMatrixFiltersState.gridlinesFrequency,
            expirationDateLimit: trainingMatrixFiltersState.expirationDateLimit,
            showCompetencyGroups: trainingMatrixFiltersState.showGroups,
            showDates: trainingMatrixFiltersState.showDates,
            exportFormat: trainingMatrixFiltersState.exportFormat,
            showGridlines: trainingMatrixFiltersState.showGridSeparator,
            showDesiredCompetencies: trainingMatrixFiltersState.showDesiredCompetencies,
            // Compliance state:
            showOverallCompliancePercentage: trainingMatrixFiltersState.showOverallCompliancePercentage,
            showPercentageOfComplianceForIndividuals:
              trainingMatrixFiltersState.showPercentageOfComplianceForIndividuals,
            showPercentageOfComplianceForCompetencies:
              trainingMatrixFiltersState.showPercentageOfComplianceForCompetencies,
            showPercentageOfComplianceForCompetencyGroups:
              trainingMatrixFiltersState.showPercentageOfComplianceForGroups,
            isManagerColumnSelected: trainingMatrixFiltersState.showManagersColumn,
            selectedColumns: trainingMatrixFiltersState.columnsToShow.map(column => column.value),
            useColorBlindPalette: trainingMatrixFiltersState.isColourBlindThemeEnabled
          },
          filters: {
            ...filters
          }
        }
      })
        .unwrap()
        .then(response => {
          if (trainingMatrixState.selectedView?.value === response.id) {
            dispatch(
              trainingMatrixActions.setSelectedView({
                value: response.id ?? '',
                text: response.title ?? ''
              })
            )
            dispatch(trainingMatrixActions.setShowSaveViewModal(false))
            toast.success('View has been updated')
          }
        })
    }
  }

  const onRenameView = (updatedView: TrainingMatrixViewDto) => {
    if (views) {
      if (!isDuplicatedViewName(updatedView.title, updatedView.id) && updatedView.id) {
        updateView({
          id: updatedView.id,
          trainingMatrixViewDto: { ...updatedView }
        })
          .unwrap()
          .then(response => {
            toast.success('View has been updated', { toasterId: manageViewsToasterId })
            if (trainingMatrixState.selectedView?.value === response.id) {
              dispatch(
                trainingMatrixActions.setSelectedView({
                  value: response.id ?? '',
                  text: response.title ?? ''
                })
              )
            }
          })
      } else {
        toast.error('A view with that name already exists', { toasterId: manageViewsToasterId })
      }
    }
  }

  const onRemoveView = (view: TrainingMatrixViewDto) => {
    if (view.id) {
      deleteView({ id: view.id })
        .unwrap()
        .then(
          () => {
            toast.success('View has been deleted', { toasterId: manageViewsToasterId })
            if (trainingMatrixState.selectedView?.value === view.id) {
              viewManagement.handleRemoveSelectedTMView()
            }
          },
          () => {
            toast.error('Error removing view', { toasterId: manageViewsToasterId })
          }
        )
    }
  }

  return (
    <>
      <SaveViewModal
        isModalVisible={trainingMatrixState.showSaveViewModal}
        viewsToManage={views ?? []}
        isLoading={isSavingView}
        onSaveView={onSaveView}
        onUpdateView={onUpdateView}
        selectedViewId={trainingMatrixState.selectedView?.value}
        onHideModal={() => dispatch(trainingMatrixActions.setShowSaveViewModal(false))}
      />
      <ViewManagementModal
        isModalVisible={trainingMatrixState.showManageViewModal}
        viewsToManage={views ?? []}
        onRemoveView={onRemoveView}
        onRenameView={onRenameView}
        onHideModal={() => dispatch(trainingMatrixActions.setShowManageViewModal(false))}
      />
    </>
  )
}

export default TrainingMatrixViewManagementModal
